// Re-exports of the `@bufbuild/protobuf` runtime + codegen types used by
// consumers of this package. Kept as a separate subpath export
// (`@github-ui/dotcom-schema/protobuf`) so the main entry can stay focused on
// generated message types and schemas without dragging runtime concerns into
// every type-only import.

import {type DescMessage, type JsonValue, type MessageShape, fromJson as protoFromJson} from '@bufbuild/protobuf'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendCustomMetric} from '@github-ui/stats'

export {create, toJson, fromJson as fromJsonStrict} from '@bufbuild/protobuf'
export type {DescMessage, JsonValue, Message, MessageInitShape, MessageShape} from '@bufbuild/protobuf'
export type {GenMessage} from '@bufbuild/protobuf/codegenv2'
export {timestampDate, timestampFromDate} from '@bufbuild/protobuf/wkt'
export {registerSchemaHash, schemaHash} from './schema-hash-registry'

/**
 * Backend signal that the JSON payload was produced from a Protobuf message
 * (via proto-JSON encoding). `fetchServiceJSON` only runs `fromJson` validation
 * when this header is `true`, allowing a single endpoint to migrate from
 * legacy JSON to proto-JSON without breaking existing callers.
 */
export const PROTO_JSON_HEADER = 'X-Proto-JSON'

export function fromJson<Desc extends DescMessage>({
  schema,
  raw,
  routeId,
  queryName,
}: {
  schema: Desc
  raw: unknown
  routeId: string
  queryName: string
}): MessageShape<Desc> {
  const start = performance.now()

  // Ignore unknown fields so that backend-only additions (fields not yet
  // present in the pinned schema) do not break consumers. This mirrors
  // the standard proto3 JSON forward-compatibility contract.
  //
  // When `react_enforce_schema` is enabled, schema mismatches surface
  // as thrown errors (failing the route's loader). When disabled, we
  // log the error and pass the raw payload through so a bad/changing
  // schema cannot break production consumers — useful while the
  // schema for a route is still being rolled out.
  try {
    const json = protoFromJson(schema, raw as JsonValue, {ignoreUnknownFields: true})

    sendCustomMetric({
      name: 'REACT_QUERY_SCHEMA_PARSE_TIME',
      value: performance.now() - start,
      tags: {
        routeId,
        queryName,
        status: 'success',
      },
    })

    return json
  } catch (error) {
    sendCustomMetric({
      name: 'REACT_QUERY_SCHEMA_ERROR',
      value: 1,
      tags: {
        routeId,
        queryName,
      },
    })

    sendCustomMetric({
      name: 'REACT_QUERY_SCHEMA_PARSE_TIME',
      value: performance.now() - start,
      tags: {
        routeId,
        queryName,
        status: 'error',
      },
    })

    if (isFeatureEnabled('react_enforce_schema')) {
      throw error
    } else {
      // Log the error without breaking the route
      setTimeout(() => {
        throw error
      })
    }

    return raw as MessageShape<Desc>
  }
}
